import * as React from 'react';
import { PaginationProps } from 'antd/es/pagination';
import { message, Table } from 'antd';
import {
    buildCompanyDataSource,
    GroupCompanyTableColumns
} from 'components/CommonGroupAndCategoryStuff/GroupsTableHelpers';
import { BasicKeyFigureInfo } from 'components/context/types';
import { apiRemoveCompanyFromGroup } from 'api/groups';
import t from 'utils/translations';
import styled from 'styled-components';
import { ColumnProps, SorterResult } from 'antd/es/table';

export enum CompanyTableSortField {
    Name = 'companyName',
    Status = 'companyStatus',
    StatementDate = 'statementDate',
    Turnover = 'turnover',
    NetProfit = 'netProfit',
    PerformerPoints = 'performerRankingPoints',
    RiskRating = 'riskRatingClass'
}

export interface CompanyTablePagination {
    currentPage: number;
    pageSize: number;
    sortField: CompanyTableSortField;
    sortOrder: 'ASC' | 'DESC';
}

const StyledTable = styled(Table)`
    background-color: white;
    td:last-of-type {
        text-align: right;
    }
`;

interface CompanyTableProps {
    afterCompanyDeleteFunction: () => void;
    companyTablePagination: CompanyTablePagination;
    setCompanyTablePagination: (
        companyTablePagination: CompanyTablePagination
    ) => void;
    fetchNewPage: (
        page: number,
        pageSize: number,
        sortField: CompanyTableSortField,
        sortOrder: 'ASC' | 'DESC'
    ) => void;
    groupId: string;
    isLoading: boolean;
    tableData: BasicKeyFigureInfo[];
    total: number;
}

function CompanyTable({
    afterCompanyDeleteFunction,
    companyTablePagination,
    fetchNewPage,
    groupId,
    isLoading,
    setCompanyTablePagination,
    tableData,
    total
}: CompanyTableProps) {
    const {
        currentPage,
        pageSize,
        sortField,
        sortOrder
    } = companyTablePagination;

    function deleteCompanyFromGroup(businessId: string) {
        apiRemoveCompanyFromGroup(groupId, businessId)
            .then(res => {
                if (res.status === 200) {
                    message.success(t('DELETE_BUSINESS_SUCCESS'));
                } else {
                    message.error(t('DELETE_BUSINESS_FAIL'));
                }
            })
            .then(() => {
                fetchNewPage(currentPage, pageSize, sortField, sortOrder);
                afterCompanyDeleteFunction();
            })
            .catch(() => {
                message.error(t('DELETE_BUSINESS_FAIL'));
            });
    }

    const dataSource = buildCompanyDataSource(
        tableData,
        deleteCompanyFromGroup
    );

    return (
        <StyledTable
            columns={GroupCompanyTableColumns}
            dataSource={dataSource}
            expandIcon={() => <></>}
            expandedRowKeys={dataSource.map((row: any) => row.key)}
            indentSize={0}
            loading={isLoading}
            onChange={(
                pagination: PaginationProps,
                filters: any,
                sorter: SorterResult<ColumnProps<any>>
            ) => {
                setCompanyTablePagination({
                    ...companyTablePagination,
                    currentPage: pagination.current || 1,
                    pageSize: pagination.pageSize || 10,
                    sortField: sorter.order
                        ? (sorter.field as CompanyTableSortField)
                        : CompanyTableSortField.Name,
                    sortOrder: sorter.order === 'descend' ? 'DESC' : 'ASC'
                });
            }}
            pagination={{
                current: currentPage,
                pageSizeOptions: ['10', '50', '100', '200'],
                showSizeChanger: true,
                total
            }}
        />
    );
}

export default CompanyTable;
